import * as React from "react";
import call from "../../call";
import { observer } from "mobx-react";
import { modalStore, AddUserModalStoreContext } from "../../store";
import axios from "axios";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import ButtonGroup from "@mui/material/ButtonGroup";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";

const MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "50%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const CHIP_ITEM_HEIGHT = 48;
const CHIP_ITEM_PADDING_TOP = 8;
const CHIP_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: CHIP_ITEM_HEIGHT * 4.5 + CHIP_ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const UserSelector = observer(() => {
  const addUserModalStore = React.useContext(AddUserModalStoreContext);
  const handleUserChange = (event) => {
    const userId = event.target.value;
    const selectedUser = addUserModalStore.users.find(
      (user) => user.id == userId,
    );
    addUserModalStore.setSelectedUser(selectedUser);
    addUserModalStore.setDeleteButtonEnabled(true);
    addUserModalStore.setSelectedRoles(selectedUser.roles);
  };

  return (
    <FormControl sx={{ width: "100%" }}>
      <InputLabel id="user-select-label">User</InputLabel>
      <Select
        labelId="user-select-label"
        id="user-select"
        label="User"
        value={
          addUserModalStore.selectedUser?.id
            ? addUserModalStore.selectedUser.id
            : ""
        }
        onChange={handleUserChange}
        disabled={addUserModalStore.users.length === 0}
      >
        {addUserModalStore.users.map((user) => (
          <MenuItem key={user.id} value={user.id}>
            {user.email}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
});

const PasswordInputs = observer(() => {
  const addUserModalStore = React.useContext(AddUserModalStoreContext);

  let helperText = "";
  if (addUserModalStore.isPasswordsNotMatch) {
    helperText = "Passwords must match";
  }
  if (addUserModalStore.isPasswordNotCorrect) {
    helperText = "Password must contain at least 8 characters";
  }
  return (
    <React.Fragment>
      <FormControl sx={{ width: "100%" }}>
        <TextField
          margin="normal"
          required
          fullWidth
          type="password"
          label="Password"
          name="password"
          onChange={(e) => addUserModalStore.setPassword(e.target.value)}
          error={
            addUserModalStore.isPasswordsNotMatch ||
            addUserModalStore.isPasswordNotCorrect
          }
          helperText={helperText}
        />
      </FormControl>
      <FormControl sx={{ width: "100%" }}>
        <TextField
          margin="normal"
          required
          fullWidth
          type="password"
          label="Password Confirm"
          name="passwordConfirm"
          onChange={(e) =>
            addUserModalStore.setPasswordConfirm(e.target.value)
          }
          error={
            !addUserModalStore.isPasswordNotCorrect &&
            addUserModalStore.isPasswordsNotMatch
          }
          helperText={!addUserModalStore.isPasswordNotCorrect && helperText}
        />
      </FormControl>
    </React.Fragment>
  );
});

const AddUserModal = observer(() => {
  const addUserModalStore = React.useContext(AddUserModalStoreContext);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!addUserModalStore.isFormValid) {
      addUserModalStore.setError(
        "Please, check the form and fix issues first.",
      );
      return;
    }
    addUserModalStore.setError("");
    addUserModalStore.setLoading(true);

    const form = new FormData(event.target);
    const data = {
      password: form.get("password"),
      roles: form.get("roles").split(","),
    };
    if (form.has("userId")) {
      data.userId = form.get("userId");
    }
    if (form.has("email")) {
      data.email = form.get("email");
    }
    call(
      axios({
        method: addUserModalStore.isInEditMode ? "put" : "post",
        url: "/api/g2/user",
        data: data,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json, text/plain",
        },
      }),
    )
      .then((response) => modalStore.closeModal())
      .catch((error) => {
        addUserModalStore.setError(error.response.data.error);
        addUserModalStore.setLoading(false);
      });
  };

  const handleSelectedRolesChange = (event) =>
    addUserModalStore.setSelectedRoles(event.target.value);

  return (
    <Modal
      open={true}
      onClose={() => modalStore.closeModal()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={MODAL_STYLE} component="form" onSubmit={handleSubmit} noValidate>
        <Stack sx={{ width: "100%" }} spacing={2}>
          {addUserModalStore.error.length > 0 ? (
            <Alert severity="error">{addUserModalStore.error}</Alert>
          ) : (
            ""
          )}
          {addUserModalStore.isInEditMode && (
            <React.Fragment>
              <UserSelector />
              <input
                type="hidden"
                value={
                  addUserModalStore.selectedUser?.id
                    ? addUserModalStore.selectedUser.id
                    : 0
                }
                name="userId"
              />
            </React.Fragment>
          )}
          {!addUserModalStore.isInEditMode && (
            <FormControl sx={{ width: "100%" }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email"
                name="email"
                autoFocus
              />
            </FormControl>
          )}
          <PasswordInputs />
          <FormControl sx={{ width: "100%" }}>
            <InputLabel id="user-roles-chip-label">Roles</InputLabel>
            <Select
              labelId="user-roles-chip-label"
              id="user-roles-chip"
              multiple
              value={addUserModalStore.selectedRoles}
              name="roles"
              onChange={handleSelectedRolesChange}
              input={
                <OutlinedInput
                  id="user-roles-multiple-chip"
                  label="User Roles"
                />
              }
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((role) => (
                    <Chip key={role} label={role} />
                  ))}
                </Box>
              )}
              MenuProps={CHIP_MENU_PROPS}
            >
              {addUserModalStore.roles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: "100%" }}>
            {addUserModalStore.isInEditMode ? (
              <ButtonGroup variant="contained">
                <LoadingButton
                  fullWidth
                  type="submit"
                  loading={addUserModalStore.isLoading}
                >
                  Save
                </LoadingButton>
              </ButtonGroup>
            ) : (
              <LoadingButton
                type="submit"
                loading={addUserModalStore.isLoading}
                variant="contained"
              >
                Save
              </LoadingButton>
            )}
          </FormControl>
        </Stack>
      </Box>
    </Modal>
  );
});

export default AddUserModal;
